import { List } from '@radix-ui/react-tabs';
import useEmblaCarousel from 'embla-carousel-react';
import {
    Children,
    cloneElement,
    ComponentPropsWithoutRef,
    ComponentPropsWithRef,
    isValidElement,
    useCallback,
    useContext,
    useLayoutEffect,
    useRef,
} from 'react';
import { cn } from '@/shared/lib/cn';
import { useInView } from '@/shared/lib/hooks/useIsInView';
import { TabsContext } from './tabs-context';
import { TabsScrollIndicator } from './tabs-scroll-indicator';
import { TabsTrigger } from './tabs-trigger';
import './tabs.scss';

type Props = ComponentPropsWithoutRef<typeof List> & {
    displayScrollChevron?: boolean;
};
export const TabsList = ({ children, className, displayScrollChevron, ...props }: Props) => {
    const { updateCSSVariables } = useContext(TabsContext);
    const [emblaRef, emblaApi] = useEmblaCarousel();
    const firstRef = useRef(null);
    const lastRef = useRef(null);
    const isLeftIndicatorVisible = !useInView(firstRef, {
        threshold: 0.5,
    });
    const isRightIndicatorVisible = !useInView(lastRef, {
        threshold: 0.25,
    });

    const handleScroll = useCallback(
        (direction: 'left' | 'right') => {
            if (!emblaApi) return;
            const selectedIndex = emblaApi.selectedScrollSnap();
            const slidesInView = emblaApi.slidesInView();
            emblaApi.scrollTo(
                selectedIndex + (direction === 'left' ? -1 : 1) * Math.round(slidesInView.length / 2),
            );
        },
        [emblaApi],
    );
    const updateAll = useCallback(() => {
        updateCSSVariables(true);
    }, [updateCSSVariables]);

    useLayoutEffect(() => {
        window.addEventListener('resize', updateAll);

        return () => {
            window.removeEventListener('resize', updateAll);
        };
    }, [updateAll]);

    return (
        <div className={cn('tabs__list-wrapper')}>
            {displayScrollChevron && isLeftIndicatorVisible && (
                <TabsScrollIndicator side="left" onClick={() => handleScroll('left')} />
            )}
            <List ref={emblaRef} className={cn('tabs__list', className)} {...props}>
                <div className="tabs__list-container" data-testid="tabs-list-container">
                    {Children.map(children, (child, index) => {
                        if (isValidElement(child)) {
                            return cloneElement(child, {
                                ref:
                                    index === 0
                                        ? firstRef
                                        : index === Children.count(children) - 1
                                          ? lastRef
                                          : undefined,
                            } as ComponentPropsWithRef<typeof TabsTrigger>);
                        }
                        return child;
                    })}
                </div>
            </List>
            {displayScrollChevron && isRightIndicatorVisible && (
                <TabsScrollIndicator side="right" onClick={() => handleScroll('right')} />
            )}
        </div>
    );
};
